/**
 * HTML Node Renderer
 *
 * Renders raw HTML content directly into the node body.
 * LOD support:
 * - full: Raw HTML content (no escaping)
 * - title: Text-only preview (tags stripped)
 * - icon: HTML icon (</>)
 * - dot: Simple dot
 */

import type { UINode, ScreenMetrics, WorldRect } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult, LODLevel } from '../renderer';
import type { MeasureOptions } from '../content-measure';
import { calculateLOD } from '../renderer';
import { hashContent } from '../texture-cache';

/** Default texture threshold in screen pixels */
const DEFAULT_TEXTURE_THRESHOLD = 60;

/**
 * Strip HTML tags to extract text-only preview.
 */
function stripTags(html: string): string {
	return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * HTML renderer for raw HTML content.
 */
export class HtmlRenderer implements NodeRenderer {
	private lastLOD: Map<string, LODLevel> = new Map();
	private lastContent: Map<string, string> = new Map();
	private contentHashes: Map<string, string> = new Map();

	render(node: UINode, metrics: ScreenMetrics, layers: RenderLayers): RenderResult {
		const screenSize = Math.max(metrics.width, metrics.height);
		const opts = layers.options;

		const lod: LODLevel = opts.forceLod
			? opts.forceLod
			: opts.simplifyLOD
				? calculateLOD(metrics, {
						enableLodFull: opts.enableLodFull,
						enableLodTitle: opts.enableLodTitle,
						enableLodIcon: opts.enableLodIcon,
						enableLodDot: opts.enableLodDot
					})
				: 'full';

		const baseContentHash = this.getContentHash(node);
		const contentHash = `${baseContentHash}:${lod}`;
		const textureId = `${node.id}:${lod}`;

		const textureThreshold = opts.textureThreshold ?? DEFAULT_TEXTURE_THRESHOLD;
		const wantTexture = (opts.forceTextureRendering || screenSize < textureThreshold) && layers.webgl !== null;
		const hasValidTexture = wantTexture && layers.webgl?.hasValidTexture(textureId, contentHash);

		const lastLod = this.lastLOD.get(node.id);
		const lodChanged = lastLod !== lod;
		const isNewElement = layers.html.isNewlyCreated(node.id);

		layers.html.setDataAttribute(node.id, 'lod', lod);

		if (hasValidTexture && layers.webgl) {
			const worldRect: WorldRect = {
				x: node.position.x,
				y: node.position.y,
				width: node.size.width,
				height: node.size.height
			};

			const textureDrawn = layers.webgl.drawCachedTexture(textureId, worldRect, 1, node.rotation ?? 0);

			if (textureDrawn) {
				layers.html.setContent(node.id, '');
				layers.html.addClass(node.id, 'texture-cached');
				this.lastContent.delete(node.id);
				this.lastLOD.set(node.id, lod);
				return { lod, changed: lodChanged };
			}
		}

		layers.html.removeClass(node.id, 'texture-cached');

		const content = this.renderContent(node, lod);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
		}

		if (wantTexture && layers.webgl && !hasValidTexture) {
			layers.webgl.requestTexture(
				textureId,
				() => layers.html.getContainer(node.id),
				contentHash,
				1,
				lod
			);
		}

		this.lastLOD.set(node.id, lod);

		return { lod, changed: lodChanged || contentChanged };
	}

	private getContentHash(node: UINode): string {
		const cached = this.contentHashes.get(node.id);
		if (cached) {
			return cached;
		}

		const contentStr = JSON.stringify(node.data);
		const hash = hashContent(contentStr);
		this.contentHashes.set(node.id, hash);
		return hash;
	}

	private renderContent(node: UINode, lod: LODLevel): string {
		const html = this.getHtml(node);

		switch (lod) {
			case 'full':
				return `
					<div class="html-node html-full">
						<div class="node-close-btn" data-action="close">&times;</div>
						<div class="html-content">${html}</div>
					</div>
				`;

			case 'title': {
				const preview = stripTags(html);
				const truncated = preview.length > 40 ? preview.slice(0, 40) + '...' : preview;
				return `
					<div class="html-node html-title-only">
						<div class="html-truncated">${this.escapeHtml(truncated)}</div>
					</div>
				`;
			}

			case 'icon':
				return `
					<div class="html-node html-icon-only">
						<span class="html-icon">&lt;/&gt;</span>
					</div>
				`;

			case 'dot':
				return `<div class="html-node html-dot"></div>`;
		}
	}

	private getHtml(node: UINode): string {
		return (
			(node.data.html as string) ||
			(node.data.content as string) ||
			''
		);
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	getContentHTML(node: UINode): string {
		return this.renderContent(node, 'full');
	}

	getMeasureOptions(node: UINode): MeasureOptions {
		const html = this.getHtml(node);
		return {
			maxWidth: 640,
			minWidth: 150,
			minHeight: 60,
			contentLength: html.length,
			preferPortrait: true,
		};
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 400,
			height: node.size?.height ?? 200
		};
	}

	cleanup(nodeId: string): void {
		this.lastLOD.delete(nodeId);
		this.lastContent.delete(nodeId);
		this.contentHashes.delete(nodeId);
	}
}
